import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle, Check, ChevronDown, ExternalLink, Hash, RefreshCcw, Search,
  Trash2, X,
} from 'lucide-react'
import Header from '@/components/layout/Header'
import SteamIcon from '@/components/site/SteamIcon'
import { Button } from '@/components/ui/button'
import { steamProfileUrl } from '@/lib/steam'
import { useAuth, isAdmin } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'

type Status = 'pending' | 'approved' | 'rejected'
type Filter = Status | 'all'

export interface ApplicationRow {
  id: number
  discord_id: string
  discord_username: string
  discord_nickname: string
  discord_avatar: string
  ingame_name: string
  steam_id: string
  age: string
  hours: string
  roles: string[]
  playstyle: string
  previous_clans: string
  referral: string
  about: string
  status: Status
  channel_id: string
  channel_url: string
  created_at: number
  reviewed_by_name: string
  reviewed_at: number
  review_note: string
}

const STATUS_STYLE: Record<Status, string> = {
  pending:  'border-amber-500/40 bg-amber-500/10 text-amber-300',
  approved: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  rejected: 'border-destructive/40 bg-destructive/10 text-destructive',
}

const INPUT_CLASS =
  'w-full rounded-sm border border-white/12 bg-white/5 px-3 py-2 text-sm text-foreground ' +
  'placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary/60'

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">{label}</p>
      <p className="mt-0.5 text-sm break-words">{children}</p>
    </div>
  )
}

/** Kartın üst satırındaki tek satırlık olgu: etiket üstte küçük, değer altta. */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground/50">{label}</p>
      <p className="truncate text-[13px] font-semibold tabular-nums">{value || '-'}</p>
    </div>
  )
}

export default function Applications() {
  const { user } = useAuth()
  const { T, lang } = useLanguage()
  const canDelete = isAdmin(user)

  const [rows, setRows] = useState<ApplicationRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<Filter>('pending')
  const [search, setSearch] = useState('')

  // Open dialog: which application, and which action
  const [action, setAction] = useState<
    { app: ApplicationRow; kind: 'approve' | 'reject' | 'delete' } | null
  >(null)
  const [note, setNote] = useState('')
  const [grantRole, setGrantRole] = useState(true)
  const [deleteChannel, setDeleteChannel] = useState(false)
  const [working, setWorking] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [creatingChannelFor, setCreatingChannelFor] = useState<number | null>(null)
  // Açık olan kart — aynı anda bir tane. Hepsi açıkken sayfa yine eski
  // yığına dönerdi; incelenen başvuru zaten birer birer incelenir.
  const [expanded, setExpanded] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/applications', { credentials: 'include' })
      if (res.ok) setRows(await res.json())
      else setError(T.applications.loadError)
    } catch {
      setError(T.applications.loadError)
    } finally {
      setLoading(false)
    }
  }, [T.applications.loadError])

  useEffect(() => { load() }, [load])

  const openAction = (app: ApplicationRow, kind: 'approve' | 'reject' | 'delete') => {
    setAction({ app, kind })
    setNote('')
    setGrantRole(true)
    setDeleteChannel(false)
    setActionError(null)
  }

  const runAction = async () => {
    if (!action) return
    setWorking(true)
    setActionError(null)
    try {
      const { app, kind } = action
      let res: Response
      if (kind === 'delete') {
        res = await fetch(
          `/api/applications/${app.id}?delete_discord_channel=${deleteChannel}`,
          { method: 'DELETE', credentials: 'include' },
        )
      } else {
        res = await fetch(`/api/applications/${app.id}/${kind}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ note, grant_academy_role: grantRole }),
        })
      }
      if (!res.ok) {
        setActionError(T.applications.actionError)
        return
      }
      setAction(null)
      await load()
    } catch {
      setActionError(T.applications.actionError)
    } finally {
      setWorking(false)
    }
  }

  const createChannel = async (app: ApplicationRow) => {
    setCreatingChannelFor(app.id)
    try {
      const res = await fetch(`/api/applications/${app.id}/channel`, {
        method: 'POST',
        credentials: 'include',
      })
      if (res.ok) {
        const updated: ApplicationRow = await res.json()
        setRows(prev => prev.map(r => (r.id === updated.id ? updated : r)))
      } else {
        setError(T.applications.channelError)
      }
    } catch {
      setError(T.applications.channelError)
    } finally {
      setCreatingChannelFor(null)
    }
  }

  const formatDate = (ts: number) =>
    ts
      ? new Date(ts * 1000).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-GB', {
          day: 'numeric', month: 'short', year: 'numeric',
        })
      : '-'

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows
      .filter(r => filter === 'all' || r.status === filter)
      .filter(r =>
        !q ||
        r.ingame_name.toLowerCase().includes(q) ||
        r.discord_nickname.toLowerCase().includes(q) ||
        r.discord_username.toLowerCase().includes(q) ||
        r.steam_id.toLowerCase().includes(q),
      )
  }, [rows, filter, search])

  const filters: { key: Filter; label: string; count: number }[] = [
    { key: 'pending',  label: T.applications.filters.pending,  count: rows.filter(r => r.status === 'pending').length },
    { key: 'approved', label: T.applications.filters.approved, count: rows.filter(r => r.status === 'approved').length },
    { key: 'rejected', label: T.applications.filters.rejected, count: rows.filter(r => r.status === 'rejected').length },
    { key: 'all',      label: T.applications.filters.all,      count: rows.length },
  ]

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <Header title={T.applications.title} />

      <div className="flex-1 space-y-5 overflow-y-auto p-4 md:p-6">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-1.5">
            {filters.map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`cursor-pointer rounded-sm border px-3 py-1.5 font-display text-[13px] font-semibold tracking-tight transition-colors ${
                  filter === f.key
                    ? 'border-primary/60 bg-primary/15 text-foreground'
                    : 'border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10'
                }`}
              >
                {f.label}
                <span className="ml-1.5 text-[11px] text-muted-foreground/70">{f.count}</span>
              </button>
            ))}
          </div>

          <div className="relative ml-auto max-w-xs flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder={T.applications.searchPlaceholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full rounded-sm border border-white/10 bg-white/5 py-1.5 pl-8 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-white/20"
            />
          </div>

          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={load} disabled={loading}>
            <RefreshCcw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            {T.applications.refresh}
          </Button>
        </div>

        {error && (
          <p className="rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        {loading && rows.length === 0 && (
          <p className="text-sm text-muted-foreground">{T.applications.loading}</p>
        )}

        {!loading && visible.length === 0 && (
          <p className="text-sm text-muted-foreground">{T.applications.empty}</p>
        )}

        {/* ── Kartlar ──────────────────────────────────────────────────
            Tam genişlikte, her cevabı açık duran yığın yerine ızgara: bir
            başvuru ekranın tamamını almaz, bekleyen liste tek bakışta okunur.

            Kartı uzatan şey serbest metinlerdi (önceki klanlar, nereden duydu,
            notlar) — onlar `Detaylar`ın altına indi. Notun ilk iki satırı
            kapalıyken de görünür: inceleme çoğu zaman orada başlar, açmadan
            karar verilecek kadarı ortada durmalı. */}
        <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
          {visible.map(app => {
            const open = expanded === app.id
            const hasDetails = !!(app.playstyle || app.previous_clans || app.referral || app.about || app.review_note)
            return (
            <div key={app.id} className="flex flex-col rounded-md border border-white/10 bg-black/25 p-3.5 backdrop-blur-sm">
              {/* Kimlik */}
              <div className="flex items-start gap-2.5">
                <img
                  src={app.discord_avatar}
                  alt=""
                  className="h-9 w-9 shrink-0 rounded-full object-cover ring-2 ring-white/10"
                  onError={e => { (e.target as HTMLImageElement).src = 'https://cdn.discordapp.com/embed/avatars/0.png' }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-display text-sm font-bold tracking-tight">{app.ingame_name}</h3>
                    <span className={`shrink-0 rounded-sm border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${STATUS_STYLE[app.status]}`}>
                      {T.applications.status[app.status]}
                    </span>
                  </div>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {app.discord_nickname}
                    {app.discord_username && ` · @${app.discord_username}`}
                  </p>
                </div>
              </div>

              {/* Olgular — yaş, saat, tarih tek satırda */}
              <div className="mt-3 grid grid-cols-3 gap-2 border-t border-white/8 pt-2.5">
                <Fact label={T.applications.fields.age} value={app.age} />
                <Fact label={T.applications.fields.hours} value={app.hours} />
                <Fact label={T.applications.fields.applied} value={formatDate(app.created_at)} />
              </div>

              {/* Roller — kapalıyken de görünür: kadroda neresi eksikse
                  başvuruya ilk o gözle bakılır. Ad veridir, büyütülmez. */}
              {app.roles.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1">
                  {app.roles.map(role => (
                    <span key={role} className="rounded-sm border border-white/10 bg-white/[0.035] px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground">
                      {role}
                    </span>
                  ))}
                </div>
              )}

              {/* Notun başı — kapalıyken iki satır */}
              {app.about && !open && (
                <p className="mt-2.5 line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">
                  {app.about}
                </p>
              )}

              {/* Detaylar */}
              {open && (
                <div className="mt-3 grid gap-3 border-t border-white/8 pt-3">
                  {app.playstyle && (
                    <Detail label={T.applications.fields.playstyle}>
                      <span className="whitespace-pre-wrap">{app.playstyle}</span>
                    </Detail>
                  )}
                  {app.previous_clans && (
                    <Detail label={T.applications.fields.previousClans}>{app.previous_clans}</Detail>
                  )}
                  {app.referral && (
                    <Detail label={T.applications.fields.referral}>{app.referral}</Detail>
                  )}
                  {app.about && (
                    <Detail label={T.applications.fields.about}>
                      <span className="whitespace-pre-wrap">{app.about}</span>
                    </Detail>
                  )}
                  {app.review_note && (
                    <Detail label={T.applications.reviewNote}>{app.review_note}</Detail>
                  )}
                  {app.reviewed_by_name && (
                    <p className="text-[11px] text-muted-foreground/70">
                      {T.applications.reviewedBy.replace('{name}', app.reviewed_by_name)}
                    </p>
                  )}
                </div>
              )}

              {hasDetails && (
                <button
                  onClick={() => setExpanded(open ? null : app.id)}
                  className="mt-2 flex items-center gap-1 self-start text-[11px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
                >
                  <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
                  {open ? T.applications.hideDetails : T.applications.details}
                </button>
              )}

              {/* Bağlantılar + eylemler. `mt-auto`: ızgarada yan yana duran
                  kartların not uzunlukları farklı, düğme satırı yine de hepsinde
                  kartın dibinde otursun. */}
              <div className="mt-auto flex flex-wrap items-center gap-1.5 border-t border-white/8 pt-3">
                {app.steam_id ? (
                  <a href={steamProfileUrl(app.steam_id)} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="h-7 gap-1.5 px-2 text-[11px]">
                      <SteamIcon size={12} />
                      {T.applications.steamProfile}
                      <ExternalLink className="h-2.5 w-2.5" />
                    </Button>
                  </a>
                ) : (
                  <span className="rounded-sm border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] font-semibold text-amber-300">
                    {T.applications.noSteam}
                  </span>
                )}

                {app.channel_url ? (
                  <a href={app.channel_url} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="h-7 gap-1.5 px-2 text-[11px]">
                      <Hash className="h-3 w-3" />
                      {T.applications.openChannel}
                    </Button>
                  </a>
                ) : app.status === 'pending' ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 gap-1.5 px-2 text-[11px]"
                    onClick={() => createChannel(app)}
                    disabled={creatingChannelFor === app.id}
                  >
                    <AlertCircle className="h-3 w-3 text-amber-400" />
                    {creatingChannelFor === app.id
                      ? T.applications.creatingChannel
                      : T.applications.createChannel}
                  </Button>
                ) : (
                  // Reviewed and its channel closed from Discord — nothing to fix.
                  <span className="text-[10px] text-muted-foreground">
                    {T.applications.channelClosed}
                  </span>
                )}

                <div className="ml-auto flex items-center gap-1.5">
                  {app.status === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        className="h-7 gap-1 bg-emerald-600 px-2 text-[11px] text-white hover:bg-emerald-600/85"
                        onClick={() => openAction(app, 'approve')}
                      >
                        <Check className="h-3 w-3" />
                        {T.applications.approve}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="h-7 gap-1 px-2 text-[11px]"
                        onClick={() => openAction(app, 'reject')}
                      >
                        <X className="h-3 w-3" />
                        {T.applications.reject}
                      </Button>
                    </>
                  )}
                  {canDelete && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      title={T.applications.delete}
                      onClick={() => openAction(app, 'delete')}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            )
          })}
        </div>
      </div>

      {/* ── Action dialog ───────────────────────────────────────────────── */}
      {action && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={() => !working && setAction(null)}
        >
          <div
            className="w-full max-w-md rounded-md border border-white/12 bg-popover p-5"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-display text-lg font-bold tracking-tight">
              {action.kind === 'approve' ? T.applications.approveTitle
                : action.kind === 'reject' ? T.applications.rejectTitle
                : T.applications.deleteTitle}
            </h3>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {action.kind === 'approve' ? T.applications.approveBody
                : action.kind === 'reject' ? T.applications.rejectBody
                : T.applications.deleteBody}
            </p>

            <p className="mt-3 rounded-sm border border-white/10 bg-white/5 px-3 py-2 text-sm">
              <span className="font-semibold">{action.app.ingame_name}</span>
              <span className="text-muted-foreground"> · {action.app.discord_nickname}</span>
            </p>

            {action.kind !== 'delete' && (
              <label className="mt-4 block">
                <span className="font-display text-[13px] font-bold tracking-tight">
                  {T.applications.noteLabel}
                </span>
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  maxLength={1000}
                  placeholder={action.kind === 'approve'
                    ? T.applications.notePlaceholder
                    : T.applications.rejectNotePlaceholder}
                  className={`${INPUT_CLASS} mt-2 min-h-20 resize-y`}
                />
              </label>
            )}

            {action.kind === 'approve' && (
              <label className="mt-3 flex cursor-pointer items-center gap-2.5 rounded-sm border border-white/12 bg-white/5 px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={grantRole}
                  onChange={e => setGrantRole(e.target.checked)}
                  className="h-4 w-4 accent-[oklch(0.5652_0.2295_28)]"
                />
                <span className="text-sm">{T.applications.grantAcademyRole}</span>
              </label>
            )}

            {action.kind === 'delete' && action.app.channel_id && (
              <label className="mt-3 flex cursor-pointer items-center gap-2.5 rounded-sm border border-white/12 bg-white/5 px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={deleteChannel}
                  onChange={e => setDeleteChannel(e.target.checked)}
                  className="h-4 w-4 accent-[oklch(0.5652_0.2295_28)]"
                />
                <span className="text-sm">{T.applications.deleteChannelToo}</span>
              </label>
            )}

            {actionError && (
              <p className="mt-3 rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {actionError}
              </p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setAction(null)} disabled={working}>
                {T.applications.cancel}
              </Button>
              <Button
                onClick={runAction}
                disabled={working}
                variant={action.kind === 'approve' ? 'default' : 'destructive'}
                className={action.kind === 'approve' ? 'bg-emerald-600 text-white hover:bg-emerald-600/85' : ''}
              >
                {working ? T.applications.working
                  : action.kind === 'approve' ? T.applications.confirmApprove
                  : action.kind === 'reject' ? T.applications.confirmReject
                  : T.applications.confirmDelete}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
